/**
 * How long is left on a job, worked out from how long jobs like it took.
 *
 * Pure, so the API and the tests agree on one rule. A percentage alone does
 * not tell somebody whether to wait or to come back after lunch; this does,
 * in words rounded to the minute, because a seconds countdown that jumps
 * about is worse than no number at all.
 */

export type Eta =
  { kind: "estimate"; seconds: number } | { kind: "typical"; seconds: number } | { kind: "slow" } | { kind: "unknown" };

/** Medians of recent finished jobs. Only kinds with at least three are present. */
export interface Typical {
  /** Seconds, for work whose length does not follow the video's. */
  seconds: Partial<Record<string, number>>;
  /** Seconds of work per second of video, for `process` and `render`. */
  perVideoSecond: Partial<Record<string, number>>;
}

export const EMPTY_TYPICAL: Typical = { seconds: {}, perVideoSecond: {} };

/** Today's wording ("about ten minutes", "a minute or two") turned into numbers. */
const FALLBACK_SECONDS: Record<string, number> = { launch: 600, translate: 60, language_voice: 120, process: 180 };
/** A render takes about as long as the video itself. */
const FALLBACK_RENDER_PER_SECOND = 1;

/** Below this, there is not enough progress to measure a pace from. */
const MEASURABLE = 0.15;
const STALL_FLOOR_MS = 120_000;
const SLOW_BEYOND = 3;

export function typicalSecondsFor(type: string, typical: Typical, videoSeconds: number | null): number | null {
  const seconds = rawTypicalSeconds(type, typical, videoSeconds);
  // A job never takes no time; a zero here means bad history, and promising
  // "less than a minute" from it would call every real job slow.
  return seconds !== null && Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function rawTypicalSeconds(type: string, typical: Typical, videoSeconds: number | null): number | null {
  const length = videoSeconds && videoSeconds > 0 ? videoSeconds : null;
  if (type === "process") {
    const rate = typical.perVideoSecond.process;
    return rate !== undefined && length !== null ? rate * length : FALLBACK_SECONDS.process;
  }
  if (type === "render") {
    if (length === null) return null;
    return (typical.perVideoSecond.render ?? FALLBACK_RENDER_PER_SECOND) * length;
  }
  return typical.seconds[type] ?? FALLBACK_SECONDS[type] ?? null;
}

export function estimateRemaining(input: {
  progress: number;
  startedAt: number | null;
  lastProgressAt: number;
  now: number;
  typicalSeconds: number | null;
}): Eta {
  const { now, typicalSeconds: typical } = input;
  const p = Math.min(1, Math.max(0, input.progress));
  if (input.startedAt === null) return typical === null ? { kind: "unknown" } : { kind: "typical", seconds: typical };
  if (p >= 1) return { kind: "estimate", seconds: 0 };

  const elapsed = Math.max(0, (now - input.startedAt) / 1000);
  if (typical !== null) {
    // A long job may go quiet for a while inside one step (a model writing a
    // page), so the window grows with what is usual for the kind.
    const stallMs = Math.max(STALL_FLOOR_MS, (typical * 1000) / 3);
    if (now - input.lastProgressAt > stallMs) return { kind: "slow" };
    if (elapsed > typical * SLOW_BEYOND) return { kind: "slow" };
    if (p < MEASURABLE) {
      // Too little done to measure a pace from: a job that has barely moved
      // by the time it usually finishes would extrapolate to hours.
      return elapsed < typical ? { kind: "typical", seconds: typical } : { kind: "slow" };
    }
  }
  if (p <= 0) return typical === null ? { kind: "unknown" } : { kind: "slow" };

  const pace = (elapsed * (1 - p)) / p;
  if (typical === null || elapsed >= typical) return { kind: "estimate", seconds: Math.round(pace) };
  // More trust in its own pace the further a job has got.
  const rest = typical - elapsed;
  return { kind: "estimate", seconds: Math.round(p * pace + (1 - p) * rest) };
}

/** Whole minutes, rounded up; null at or beyond 55, which reads as an hour. */
function minutes(seconds: number): number | null {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return m >= 55 ? null : m;
}

export function etaLabel(eta: Eta | null | undefined): string | null {
  if (!eta || eta.kind === "unknown") return null;
  if (eta.kind === "slow") return "Taking longer than usual";
  const m = minutes(eta.seconds);
  if (eta.kind === "typical") {
    if (m === null) return "Usually about an hour";
    return m === 1 ? "Usually about a minute" : `Usually about ${m} minutes`;
  }
  if (eta.seconds < 60) return "Less than a minute left";
  if (m === null) return "About an hour left";
  return m === 1 ? "About a minute left" : `About ${m} minutes left`;
}

export function etaShort(eta: Eta | null | undefined): string | null {
  if (!eta || eta.kind === "unknown") return null;
  if (eta.kind === "slow") return "Slow";
  const m = minutes(eta.seconds);
  if (eta.kind === "typical") return m === null ? "~1 hr" : `~${m} min`;
  if (eta.seconds < 60) return "<1 min left";
  return m === null ? "~1 hr left" : `${m} min left`;
}

/** What a kind of job is doing, as a whole. Cosmetic kinds have no words on purpose. */
const KIND_LABELS: Record<string, string> = {
  process: "Processing the recording",
  script: "Writing the script",
  voice: "Recording the narration",
  autocut: "Rebuilding the cut",
  proxy: "Preparing the preview",
  vision: "Looking at the recording",
  render: "Rendering the video",
  launch: "Making the launch video",
  translate: "Translating",
  language_voice: "Voicing the script",
};

/** The steps worth naming, by kind, as the worker reports them. */
const STEP_LABELS: Record<string, Record<string, string>> = {
  launch: { writing: "Writing the film", rendering: "Rendering the film", done: "Finishing" },
  translate: { translating: "Translating", translated: "Translated" },
  language_voice: {
    voicing: "Voicing the script",
    mixing: "Mixing the sound",
    drawing: "Redrawing every word on screen",
    storing: "Finishing",
    charging: "Finishing",
  },
  render: { bundling: "Getting ready", rendering: "Rendering the video" },
};

export function jobLabel(type: string, step: string | null): string | null {
  return (step ? STEP_LABELS[type]?.[step] : undefined) ?? KIND_LABELS[type] ?? null;
}
